import { FinancialRowTypes, IOngoingReportingFinancialDates, OngoingReportingType } from './bbc'

// ---------------------------------------------------------------------------
// The datasets a QuickBooks pull can produce.
//
// One member per data source, and every source has exactly one puller behind it
// (see services/qbo/reporting). Callers ask for datasets, never for endpoints —
// which report(s) a dataset costs is an implementation detail of its puller.
// Declared in display order: statements, then aging, then transaction-level detail.
// ---------------------------------------------------------------------------
export enum QboDataset {
  BalanceSheet = 'BalanceSheet',
  IncomeStatement = 'IncomeStatement',
  ArAging = 'ArAging',
  ApAging = 'ApAging',
  GeneralLedger = 'GeneralLedger',
  TrialBalance = 'TrialBalance',
  TopCustomers = 'TopCustomers',
  TopVendors = 'TopVendors',
}

// Where in the client lifecycle a pull is happening. Each preset fixes the
// dataset list and the history depth that stage of the lifecycle expects, so a
// caller states its intent ("this is the nightly portfolio run") instead of
// restating twelve-vs-sixty-vs-one-hundred-and-twenty months at every call site.
export enum QboPullPreset {
  // Nightly portfolio refresh for a Current client.
  Portfolio = 'Portfolio',
  // Prospect / business-development pull, deeper history plus current aging.
  BusinessDevelopment = 'BusinessDevelopment',
  // Due-diligence pull. Deepest history; aging is replayed month by month.
  Underwriting = 'Underwriting',
}

// QuickBooks reports every figure on one basis or the other, and the choice is
// ours to make explicitly — Codat's financials endpoints never exposed it.
export enum QboAccountingMethod {
  Accrual = 'Accrual',
  Cash = 'Cash',
}

// ---------------------------------------------------------------------------
// Normalized model
//
// This is the contract the rest of Atlas consumes. It is deliberately NOT the
// QuickBooks response shape and NOT a spreadsheet: a workbook is one possible
// rendering of these rows (see reporting/workbook.ts), never the source.
// ---------------------------------------------------------------------------

// One line of a financial statement, with one amount per period column.
//
// Field-for-field compatible with `FinancialsResponse` (common/financials), which
// is what the AI spreadsheet parser produces for uploaded statements — so a
// QuickBooks-sourced statement lands in `financials_mappings_raw` through exactly
// the same path as an uploaded one, with no parsing step in between.
export interface IQboStatementRow {
  // Display name of the line, account number prefixed when QuickBooks reports one.
  account: string
  // Reserved for the account-category prediction the uploaded-file path fills in.
  // QuickBooks tells us the section outright, so this is empty on a direct pull.
  prediction: string
  // 1-based order the line appears in the statement. Statements are read
  // top-to-bottom; position is what preserves that after any grouping.
  position: number
  // Indent level, 1-based. Section headers sit at their own depth and their
  // members one deeper.
  depth: number
  tags: { tag: string }[]
  values: { period: string; amount: number }[]
  rowType: FinancialRowTypes
  // QuickBooks Account.Id when the line is a real account (absent on headers,
  // totals and computed lines like Gross Profit).
  accountId: string | null
  // Top-level statement section this line belongs to, from QuickBooks' own row
  // grouping: Income, COGS, Expenses, Assets, Liabilities, Equity, …
  section: string | null
}

// A statement as pulled: the rows plus the period columns they are keyed on.
export interface IQboStatement {
  statementType: OngoingReportingType.BalanceSheet | OngoingReportingType.IncomeStatement
  // Period keys in ascending order, `YYYY-MM`. Every row carries a value for
  // each of these (zero-filled), so the set is safe to use as column headers.
  periods: string[]
  startDate: string
  endDate: string
  accountingMethod: QboAccountingMethod
  currency: string | null
  rows: IQboStatementRow[]
}

// One open invoice (AR) or bill (AP) as of a given date.
//
// Field names mirror `client_ar_raw` / `client_ap_raw` so a row inserts without a
// translation layer; `counterparty` is the one deliberate exception, because the
// two tables disagree (debtor vs creditor) and a shared shape has to pick one.
export interface IQboAgingRow {
  asOfDate: string
  // Customer for AR, vendor for AP. Maps to `debtor` / `creditor` on insert.
  counterparty: string
  invoice: string | null
  invoiceDate: string
  dueDate: string | null
  balance: number
  // QuickBooks transaction type (Invoice, Credit Memo, Bill, Vendor Credit, …).
  transactionType: string | null
  // QuickBooks entity Id of the transaction. Together with `transactionType` it is
  // the key the document behind the row is fetched by.
  qboTxnId: string | null
  memo: string | null
  // Aging bucket as QuickBooks itself computed it, so our own day-count and
  // theirs can be reconciled rather than silently diverging.
  agingBucket: string | null
  daysPastDue: number | null
}

export interface IQboAging {
  type: QboTransactionSide
  asOfDate: string
  rows: IQboAgingRow[]
}

// One general-ledger line. Mirrors `client_general_ledger`.
export interface IQboLedgerRow {
  recordDate: string
  account: string
  accountId: string | null
  // QuickBooks transaction type (Invoice, Bill Payment, Journal Entry, …).
  type: string
  documentNumber: string | null
  // Customer/vendor on the transaction, as QuickBooks reports it.
  counterparty: string | null
  memo: string | null
  debit: number
  credit: number
  // Signed net movement: debit positive, credit negative. This is the `amount`
  // column of `client_general_ledger`.
  amount: number
  // Running balance for the account, as QuickBooks computed it. Null when the
  // report did not carry a balance column for the row.
  balance: number | null
}

export interface IQboLedger {
  startDate: string
  endDate: string
  accountingMethod: QboAccountingMethod
  rows: IQboLedgerRow[]
}

// One account's debit/credit totals for a period.
export interface IQboTrialBalanceRow {
  account: string
  accountId: string | null
  debit: number
  credit: number
}

export interface IQboTrialBalance {
  startDate: string
  endDate: string
  accountingMethod: QboAccountingMethod
  rows: IQboTrialBalanceRow[]
}

// A customer's sales (or a vendor's spend) broken out by month.
export interface IQboCounterpartyTotalsRow {
  name: string
  values: { period: string; amount: number }[]
  total: number
}

export interface IQboCounterpartyTotals {
  type: QboDataset.TopCustomers | QboDataset.TopVendors
  startDate: string
  endDate: string
  periods: string[]
  rows: IQboCounterpartyTotalsRow[]
}

// ---------------------------------------------------------------------------
// Pull request / result
// ---------------------------------------------------------------------------

export interface IQboPullOptions {
  clientName: string
  // Datasets to pull. Overrides the preset's list while keeping its history
  // depth — narrowing to one dataset is the common case.
  datasets?: QboDataset[]
  // Lifecycle stage. Supplies the dataset list and history depth; any explicit
  // field below still wins over the preset's default.
  preset?: QboPullPreset
  // Anchor date for the whole pull. Defaults to today.
  asOfDate?: string
  // Months of history for statements and counterparty totals.
  periods?: number
  // Explicit aging as-of dates. When omitted, aging is pulled at `asOfDate` only;
  // the Underwriting preset expands this to a run of month-ends.
  arAgingDates?: string[]
  apAgingDates?: string[]
  // How many month-end snapshots of aging to take, counting back from `asOfDate`.
  // The count a caller states; `arAgingDates` is the expansion of it.
  arAgingMonths?: number
  apAgingMonths?: number
  // Ledger window. Defaults to `ledgerMonths` back from `asOfDate`, else `periods`.
  ledgerStartDate?: string
  ledgerEndDate?: string
  ledgerMonths?: number
  accountingMethod?: QboAccountingMethod
  // Cap on rows returned per counterparty dataset.
  topCounterpartyLimit?: number
  // Write the pull to qbo_pull_run / qbo_report_snapshot. On by default: the
  // stored copy is the point, and a caller has to opt out deliberately (a
  // read-only diff against Codat, say) rather than forget to opt in.
  persist?: boolean
  // Recorded on the run when a person triggered the pull.
  triggeredByUserId?: string | null
}

// Why a requested dataset produced nothing. Distinguishing these matters: a
// client with no bills is not the same as a QuickBooks call that failed, and the
// two used to arrive at the notification layer as the same empty result.
export enum QboPullFailureReason {
  ApiError = 'ApiError',
  NotConnected = 'NotConnected',
  NoData = 'NoData',
}

export interface IQboPullFailure {
  dataset: QboDataset
  reason: QboPullFailureReason
  message: string
  // The as-of date the failure belongs to, for datasets pulled once per date.
  asOfDate?: string
}

// Everything a pull produced. Datasets that were not requested are absent;
// datasets that were requested and failed appear in `failures`, never as a
// silently empty array.
export interface IQboPullResult {
  clientName: string
  realmId: string
  asOfDate: string
  accountingMethod: QboAccountingMethod
  balanceSheet?: IQboStatement
  incomeStatement?: IQboStatement
  // One entry per requested as-of date, in the order requested.
  arAging?: IQboAging[]
  apAging?: IQboAging[]
  generalLedger?: IQboLedger
  trialBalance?: IQboTrialBalance
  topCustomers?: IQboCounterpartyTotals
  topVendors?: IQboCounterpartyTotals
  failures: IQboPullFailure[]
}

// What a pull filed as ongoing reporting, when the caller asked it to. Mirrors
// the row the Codat portfolio sync writes, so the pre-processing queue and the
// reporting dashboard see no difference between the two sources.
export interface IQboOngoingReportingFiling {
  ongoingReportingId: string
  boxFileId: string
  fileName: string
  recordDate: string
  financialDates: IOngoingReportingFinancialDates
}

// The pull plus whatever it filed. `ongoingReporting` is null when submission
// was not requested, and equally when it was requested but the pull produced
// neither statement — the failures array says which.
export interface IQboPullResponse extends IQboPullResult {
  ongoingReporting: IQboOngoingReportingFiling | null
}

// ---------------------------------------------------------------------------
// Invoices and bills (synced from QuickBooks entities), and the documents behind them
// ---------------------------------------------------------------------------

// AR = invoices and credit memos; AP = bills and vendor credits.
export type QboTransactionSide = OngoingReportingType.AR | OngoingReportingType.AP

// Derived from balance and void state, never stored: a payment in QuickBooks
// changes it, and the next sync picks that up through the balance.
export enum QboTransactionStatus {
  Open = 'Open',
  PartiallyPaid = 'PartiallyPaid',
  Paid = 'Paid',
  Voided = 'Voided',
  // Credits (credit memos, vendor credits) are applied rather than paid.
  Unapplied = 'Unapplied',
  Applied = 'Applied',
}

export enum QboDocumentSource {
  // The PDF QuickBooks renders for a sales form (invoice, credit memo, …).
  Generated = 'Generated',
  // A file someone attached to the transaction in QuickBooks.
  Attachment = 'Attachment',
}

export interface IQboDocument {
  id: string
  fileName: string
  boxLink: string
  source: QboDocumentSource
}

export interface IQboTransactionRow {
  id: string
  side: QboTransactionSide
  txnType: string
  qboTxnId: string
  docNumber: string | null
  counterpartyName: string | null
  txnDate: string
  dueDate: string | null
  totalAmount: number
  balance: number
  currency: string | null
  memo: string | null
  status: QboTransactionStatus
  daysPastDue: number
  // Null once nothing is owed (paid, applied or voided).
  agingBucket: string | null
  // Null until someone asks for the documents; empty once QuickBooks had none.
  documents: IQboDocument[] | null
}

export interface IQboTransactionsResponse {
  data: IQboTransactionRow[]
  // Over the filtered set, not just this page.
  totals: { totalItems: number; totalBalance: number }
  // When invoices and bills were last synced; null before the first sync.
  syncedAt: string | null
  // Until then Refresh answers without calling QuickBooks; null when it may call.
  refreshAvailableAt: string | null
}
